// TODO-KPI-03 — Set Yönetimi orta panel üstü "Katalogda N güncelleme var" bandı + açılır panel.
// Durum `GET /kpi/sets/:setId/catalog-sync`; satır başına "Uygula" ya da "Tümünü uygula"
// (`POST …/apply`, kpi.manage); "Hepsini yoksay" sunucuya gitmez — o anki fark imzası
// localStorage'da (lib/kpi-catalog-sync). Kaydedilmemiş taslak varken (blocked) uygulama kilitli:
// uygulama sunucuyu değiştirir, üst bileşen seti yeniden yükler (taslak kaybolmasın).
// Birim varyantı: "Birim" satırı müşteri genelindeki kaydı söyler ("müşteri genelinde özel birim: X
// (katalog: Y)"); uygulama sunucuda unit-change yolundan geçer — kullanımda çevrim onayı gerekince
// satır uygulanamaz, hatanın yanında "Göstergeyi aç" (IndicatorModal'da mod seçilerek değiştirilir).
// Kırılım: `new_metric` satırı katalogda grup/üye ise "Yeni kırılım grubu/üyesi" + "X göstergesine
// yeni kırılım (grup/üye): Y" (`catalog` verilirse; uygulama aynı new_metric yolu).
// Sektör paketleri DR7: görünüm `CatalogSyncBand`'de (paket kipi + eski bant); yük yalnız eyleme açık
// satırlar (`applyPayload`), bilgi satırı (paketten çıkarılan) asla gönderilmez; imza sunucudan.

import { useCallback, useEffect, useMemo, useState } from "react";
import type { KpiCatalogChange, KpiCatalogSyncStatus, KpiMetric } from "@mahir/shared";
import { applyKpiCatalogSync, getKpiCatalogSync } from "../../api/kpi-catalog-sync";
import {
  KPI_CATALOG_SYNC_COPY as COPY,
  applyOutcome,
  applyPayload,
  isCatalogSyncDismissed,
  kpiCatalogSyncStorageKey,
  loadDismissedSignature,
  saveDismissedSignature,
} from "../../lib/kpi-catalog-sync";
import { CatalogSyncBand } from "./CatalogSyncBand";

export interface CatalogSyncPanelProps {
  setId: string;
  userId: string;
  canEdit: boolean;
  /** Kaydedilmemiş taslak varken uygulama kilitli (sunucu değişince set yeniden yüklenir). */
  blocked: boolean;
  /** Uygulama sonrası (en az bir satır yazıldıysa) — üst bileşen seti yeniden yükler. */
  onApplied: (message: string) => void;
  /** birim satırı uygulanamadıysa göstergeyi pencerede aç (set göstergesi id) */
  onOpenIndicator?: (indicatorId: string) => void;
  /** katalog (kırılım grup/üye önerisinin dili için); yoksa düz "Yeni gösterge" */
  catalog?: readonly KpiMetric[] | null | undefined;
}

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function CatalogSyncPanel(props: CatalogSyncPanelProps): JSX.Element | null {
  const { setId, userId, canEdit, blocked, onApplied } = props;
  const [status, setStatus] = useState<KpiCatalogSyncStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [applying, setApplying] = useState<string | null>(null);
  const [failed, setFailed] = useState<Record<string, string>>({});
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const key = useMemo(() => kpiCatalogSyncStorageKey(userId, setId), [userId, setId]);
  const catalogById = useMemo(
    () => new Map((props.catalog ?? []).map((m) => [m.id, m])),
    [props.catalog],
  );

  const load = useCallback(async () => {
    try {
      const s = await getKpiCatalogSync(setId);
      setStatus(s);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : COPY.loadFailed);
    }
  }, [setId]);

  useEffect(() => {
    setStatus(null);
    setOpen(false);
    setFailed({});
    setInfoOpen(false);
    setResult(null);
    setDismissed(loadDismissedSignature(storage(), key));
    void load();
  }, [load, key]);

  if (error) {
    return (
      <div role="status" className="text-xs mb-3" style={{ color: "var(--color-text-2)" }}>
        {COPY.loadFailed}: {error}
      </div>
    );
  }
  if (!status || status.changes.length === 0) return null;

  async function apply(items: KpiCatalogChange[], tag: string): Promise<void> {
    const payload = applyPayload(items);
    if (!canEdit || blocked || payload.length === 0) return;
    const before = status?.changes ?? [];
    setApplying(tag);
    try {
      const res = await applyKpiCatalogSync(setId, { items: payload });
      const errs: Record<string, string> = {};
      for (const f of res.failed) errs[`${f.kind}:${f.metricId}`] = f.message;
      setFailed(errs);
      setStatus(res.status);
      if (res.status.changes.length === 0) setOpen(false);
      // kip uygulama anındaki durumdan (applyOutcome) — tek katalog satırı da sonuç satırını günceller
      const { text, bandResult } = applyOutcome(before, items, res);
      setResult(bandResult);
      if (res.applied > 0) onApplied(text);
    } catch (err) {
      setError(err instanceof Error ? err.message : COPY.loadFailed);
    } finally {
      setApplying(null);
    }
  }

  return (
    <CatalogSyncBand
      status={status}
      canEdit={canEdit}
      blocked={blocked}
      applying={applying}
      failed={failed}
      dismissed={isCatalogSyncDismissed(status.signature, dismissed)}
      open={open}
      infoOpen={infoOpen}
      result={result}
      catalogById={catalogById}
      onToggleOpen={() => setOpen((o) => !o)}
      onToggleInfo={() => setInfoOpen((o) => !o)}
      onApply={(items, tag) => void apply(items, tag)}
      onDismissAll={() => {
        saveDismissedSignature(storage(), key, status.signature);
        setDismissed(status.signature);
      }}
      onRestore={() => {
        saveDismissedSignature(storage(), key, null);
        setDismissed(null);
      }}
      onOpenIndicator={props.onOpenIndicator}
    />
  );
}
